import type * as React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { cn } from "@/lib/utils";
import { Label } from "@/v2/ui/primitives/label";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "@/v2/ui/primitives/searchable-select";

export interface FormSearchableSelectProps {
  name: string;
  label?: string;
  options: SearchableSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  required?: boolean;
  disabled?: boolean;
  allowAll?: boolean;
  allLabel?: string;
  renderOption?: (option: SearchableSelectOption) => React.ReactNode;
  renderSelected?: (option: SearchableSelectOption) => React.ReactNode;
  className?: string;
}

export const FormSearchableSelect: React.FC<FormSearchableSelectProps> = ({
  name,
  label,
  options,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  required = false,
  disabled = false,
  allowAll = false,
  allLabel,
  renderOption,
  renderSelected,
  className,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const error = errors[name];
  const errorMessage = error?.message as string | undefined;

  return (
    <div className={cn("tw-space-y-1", className)}>
      {label && (
        <Label htmlFor={name}>
          {label}
          {required && <span className="tw-text-destructive tw-ml-1">*</span>}
        </Label>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <SearchableSelect
            value={field.value}
            onChange={field.onChange}
            options={options}
            placeholder={placeholder}
            searchPlaceholder={searchPlaceholder}
            emptyMessage={emptyMessage}
            disabled={disabled}
            allowAll={allowAll}
            allLabel={allLabel}
            renderOption={renderOption}
            renderValue={renderSelected}
            className={cn(
              error && "tw-border-destructive focus:tw-ring-destructive"
            )}
          />
        )}
      />
      {errorMessage && (
        <p className="tw-text-sm tw-text-destructive tw-mt-1">{errorMessage}</p>
      )}
    </div>
  );
};

export type { SearchableSelectOption };
